import { DateTime } from 'luxon';

export const DateUtils = {
  /**
   * Get today's date in YYYY-MM-DD format
   */
  today: (): string => {
    return DateTime.now().toISODate() || '';
  },

  /**
   * Format a date to YYYY-MM-DD
   */
  toISODate: (date: Date | string | DateTime): string => {
    if (date instanceof DateTime) {
      return date.toISODate() || '';
    }
    return DateTime.fromJSDate(new Date(date)).toISODate() || '';
  },

  /**
   * Format a database date to a date to display.
   */
  toDisplayDate: (date: string | null | undefined): string => {
    if (!date) return '';

    const dateTime = DateUtils.parseDate(date);
    return dateTime.toFormat('M/d/yyyy');
  },

  /**
   * Convert ISO date string to HTML date input format (YYYY-MM-DD)
   */
  toDateInputFormat: (isoString: string | null | undefined): string => {
    if (!isoString) return '';

    const dateTime = DateUtils.parseDate(isoString);
    return dateTime.isValid ? dateTime.toFormat('yyyy-MM-dd') : '';
  },

  /**
   * Convert HTML date input format (YYYY-MM-DD) to ISO date string
   */
  fromDateInputFormat: (dateString: string): string | null => {
    if (!dateString) return null;

    const dateTime = DateTime.fromFormat(dateString, 'yyyy-MM-dd');
    return dateTime.isValid ? dateTime.toISODate() : null;
  },

  /**
   * Parse a date string and return a DateTime object in local timezone
   * If the string doesn't include timezone info, assumes UTC and converts to local
   */
  parseDate: (dateString: string): DateTime => {
    // If the datetime string contains 'T' (indicating ISO datetime format)
    // but doesn't have timezone info (no 'Z' or timezone offset like '+00:00'),
    // treat it as UTC since the backend stores UTC times without timezone markers,
    // then convert to local timezone
    if (
      dateString.includes('T') &&
      !dateString.endsWith('Z') &&
      !/[+-]\d{2}:\d{2}$/.test(dateString)
    ) {
      return DateTime.fromISO(dateString, { zone: 'utc' }).toLocal();
    }

    return DateTime.fromISO(dateString);
  },

  /**
   * Check if a date string is valid
   */
  isValidDate: (dateString: string): boolean => {
    return DateTime.fromISO(dateString).isValid;
  },

  /**
   * Format a database datetime to display with date and time.
   * Converts from UTC to local timezone.
   */
  toDisplayDateTime: (date: string | null | undefined): string => {
    if (!date) return '';

    const dateTime = DateUtils.parseDate(date);
    return dateTime.toFormat('M/d/yyyy, h:mm:ss a');
  },

  /**
   * Convert a local calendar date (YYYY-MM-DD from a date input) to a UTC ISO string
   * at local start-of-day. Avoids the off-by-one-day shift caused by `new Date('YYYY-MM-DD')`,
   * which parses as UTC midnight.
   */
  localDateInputToUtcIso: (dateString: string): string => {
    const dateTime = DateTime.fromFormat(dateString, 'yyyy-MM-dd').startOf('day');
    return dateTime.toUTC().toISO() ?? '';
  },

  /**
   * Display a production completion date entered as date-only.
   * Legacy rows stored as UTC midnight use the UTC calendar date; newer rows use local date.
   */
  toDisplayProductionDate: (date: string | null | undefined): string => {
    if (!date) return '';

    const dateTime = DateTime.fromISO(date, { setZone: true });
    if (!dateTime.isValid) return '';

    const utc = dateTime.toUTC();
    if (utc.hour === 0 && utc.minute === 0 && utc.second === 0 && utc.millisecond === 0) {
      return utc.toFormat('M/d/yyyy');
    }

    return dateTime.toLocal().toFormat('M/d/yyyy');
  },
};
